<template>
  <div class="ida-archive-search row" v-if="canRead">
    <div class="col-md-12 card advanced-search">
      <div class="card-header">
        <h4 class="card-title" style="display:inline">
          IDA Archive Search
        </h4>
        <span class="text-danger" v-show="errors.has('idaArchive')">
          {{ mainSearchFieldErrorText }}
        </span>
      </div>
      <div class="card-body form-group">
        <div class="row">
          <div class="col-md-3">
            <label>Client</label>
            <el-autocomplete
              style="width:100%"
              class="inline-input"
              clearable
              name="idaClientName"
              ref="idaClientName"
              :debounce="100"
              v-model="selectedIdaClient"
              :fetch-suggestions="findIdaClients"
              :trigger-on-focus="false"
              value-key="name"
              @select="setIdaClient"
              @blur="verifyIdaClientAutocompleteSelected"
              placeholder="Client"
              data-vv-name="idaClient">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaClientName')">
              {{ fieldNotValidError('Client') }}
            </small>
          </div>
          <div class="col-md-3">
            <label>Builder
            </label>
            <el-autocomplete
              style="width:100%"
              class="inline-input"
              clearable
              name="idaBuilderName"
              ref="idaBuilderName"
              :debounce="100"
              v-model="selectedIdaBuilder"
              :fetch-suggestions="findIdaBuilders"
              :trigger-on-focus="false"
              value-key="name"
              @select="setIdaBuilder"
              @blur="verifyIdaBuilderAutocompleteSelected"
              placeholder="Builder"
              data-vv-name="idaBuilder">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaBuilderName')">
              {{ fieldNotValidError('Builder') }}
            </small>
          </div>
          <div class="col-md-2">
            <label>ID</label>
            <el-input type="search"
                      name="id"
                      clearable
                      placeholder="ID"
                      v-model="query.idaId"
                      data-vv-name="search"/>
          </div>
          <div class="col-md-2">
            <label>Invoice Number</label>
            <el-input type="search"
                      name="invoiceNumber"
                      clearable
                      placeholder="Invoice Number"
                      v-model="query.invoiceNumber"
                      data-vv-name="search"/>
          </div>
          <div class="col-md-2">
            <label>Price</label>
            <el-input type="search"
                      name="price"
                      clearable
                      placeholder="Price"
                      v-model="query.price"
                      data-vv-name="search"/>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-2">
            <label>Street Number
            </label>
            <el-input type="search"
                      name="streetNumber"
                      clearable
                      placeholder="Street Number"
                      v-model="query.streetNumber"
                      data-vv-name="search"/>
          </div>
          <div class="col-md-3">
            <label>Street Name</label>
            <el-autocomplete
              style="width:100%"
              class="inline-input"
              clearable
              name="idaStreetName"
              ref="idaStreetName"
              :debounce="100"
              v-model="selectedIdaStreet"
              :fetch-suggestions="findIdaStreets"
              :trigger-on-focus="false"
              value-key="name"
              @select="setIdaStreet"
              @blur="verifyIdaStreetAutocompleteSelected"
              placeholder="Street"
              data-vv-name="idaStreet">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaStreetName')">
              {{ fieldNotValidError('Street') }}
            </small>
          </div>
          <div class="col-md-2">
            <label>City</label>
            <v-select
              v-model="query.idaCity"
              placeholder="City"
              data-vv-name="search"
              :options="constants.IDA.CITIES">
            </v-select>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaCity')">
              {{ fieldNotValidError('City') }}
            </small>
          </div>
          <div class="col-md-2">
            <label>State</label>
            <el-select name="state"
                       class="select"
                       size="large"
                       placeholder="State"
                       data-vv-as="state"
                       v-model="query.state">
              <el-option v-for="option in constants.IDA.STATES"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
          </div>
          <div class="col-md-3">
            <label>Subdivision</label>
            <el-autocomplete
              style="width:100%"
              class="inline-input"
              clearable
              name="idaSubdivisionName"
              ref="idaSubdivisionName"
              :debounce="100"
              v-model="selectedIdaSubdivision"
              :fetch-suggestions="findIdaSubdivisions"
              :trigger-on-focus="false"
              value-key="name"
              @select="setIdaSubdivision"
              @blur="verifyIdaSubdivisionAutocompleteSelected"
              placeholder="Subdivision"
              data-vv-name="idaSubdivision">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaSubdivisionName')">
              {{ fieldNotValidError('Subdivision') }}
            </small>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-4">
            <label>Inspection Type</label>
            <v-select
              v-model="query.idaInspectionType"
              placeholder="Inspection Type"
              data-vv-name="search"
              :options="constants.IDA.INSPECTION_TYPES">
            </v-select>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaInspectionType')">
              {{ fieldNotValidError('Inspection Type') }}
            </small>
          </div>
          <div class="col-md-3">
            <label>Inspected By</label>
            <v-select
              v-model="query.idaInspector"
              placeholder="Inspected By"
              data-vv-name="search"
              :options="constants.IDA.INSPECTORS">
            </v-select>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('idaInspectorId')">
              {{ fieldNotValidError('Inspector') }}
            </small>
           </div>
           <div class="col-md-2">
             <label>Inspection Result</label>
             <v-select multiple class="select width-100"
                        size="large"
                        placeholder="Inspection Result"
                        clearable
                        data-vv-name="search"
                        v-model="query.inspectionResult"
                        :options="constants.IDA.INSPECTION_RESULTS">
             </v-select>
           </div>
          <div class="col-md-3">
            <label>Scheduled By
            </label>
            <v-select
              v-model="query.idaScheduler"
              placeholder="Scheduled By"
              value="value"
              label="name"
              data-vv-name="search"
              :options="constants.IDA.SCHEDULERS">
            </v-select>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('Id')">
              {{ fieldNotValidError('Scheduler') }}
            </small>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-3">
            <label>Requested Inspection Date</label>
            <el-date-picker type="daterange"
                            unlink-panels
                            range-separator="-"
                            start-placeholder="Start date"
                            end-placeholder="End date"
                            v-model="query.requestedInspectionDate"
                            :format="constants.DATE.FORMAT.DISPLAY"
                            :value-format="constants.DATE.FORMAT.VALUE"
                            :picker-options="datePickerConfig"
                            placeholder="Requested Inspection Date">
            </el-date-picker>
          </div>
          <div class="col-md-3">
            <label>Date Scheduled</label>
            <el-date-picker type="daterange"
              unlink-panels
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              v-model="query.dateScheduled"
              :format="constants.DATE.FORMAT.DISPLAY"
              :value-format="constants.DATE.FORMAT.VALUE"
              :picker-options="datePickerConfig"
              placeholder="Date Scheduled">
            </el-date-picker>
          </div>
          <div class="col-md-3">
            <label>Actual Inspection Date</label>
            <el-date-picker type="daterange"
              unlink-panels
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              v-model="query.actualInspectionDate"
              :format="constants.DATE.FORMAT.DISPLAY"
              :value-format="constants.DATE.FORMAT.VALUE"
              :picker-options="datePickerConfig"
              placeholder="Actual Inspection Date">
            </el-date-picker>
          </div>
        </div>
      </div><!-- card content -->
      <div class="card-footer row">
        <div class="col-md-12">
          <button type="button" @click="fetchSearchResults" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" id="btn-clear" @click="clearSearchAndResults" class="btn btn-fill btn-info pull-right btn-clear">
            Clear
          </button>
        </div>
      </div>
      <div class="fix-search-container"></div>
    </div><!-- end card -->
    <div class="col-md-12 card">
      <div class="card-content row">
        <div class="col-sm-12" style="padding: 12px;">
          <div style="margin-bottom: 12px;">
              <div class="pull-right" style="margin-bottom: 12px;">
                <a @click.prevent="downloadExcel" v-if="hasResults" href="#"><img src="static/img/icon-excel.jpg" title="Export to Excel" class="m-left-12"/></a>
              </div>
          </div>
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    v-else
                    :header-cell-class-name="headerSortClass"
                    :data="sortedTableData"
                    :key="tableKey"
                    border
                    style="width: 100%"
                    :max-height="tableHeight(400)">
            <el-table-column
              :width=this.constants.INDEX_COLUMN_WIDTHS
              align="center"
              label="#"
              type="index" />
            <el-table-column key="ID" min-width="105" label="ID" :sortable="true" prop="_source.idaId" sort-by="_source.idaId" />
            <el-table-column key="Client Name" min-width="175" label="Client Name" :sortable="true" prop="_source.clientName" sort-by="_source.clientName" />
            <el-table-column key="Client Contact" min-width="175" label="Client Contact" :sortable="true" prop="_source.clientContact" sort-by="_source.clientContact" />
            <el-table-column key="Builder Name" min-width="175" label="Builder Name" :sortable="true" prop="_source.builderName" sort-by="_source.builderName" />
            <el-table-column key="Subdivision Name" min-width="190" label="Subdivision Name" :sortable="true" prop="_source.subdivisionName" sort-by="_source.subdivisionName" />
            <el-table-column key="Street Number" min-width="170" label="Street Number" :sortable="true" prop="_source.streetNumber" sort-by="_source.streetNumber" />
            <el-table-column key="Street Name" min-width="170" label="Street Name" :sortable="true" prop="_source.streetName" sort-by="_source.streetName" />
            <el-table-column key="City" min-width="165" label="City" :sortable="true" prop="_source.city" sort-by="_source.city" />
            <el-table-column key="State" min-width="120" label="State" :sortable="true" prop="_source.state" sort-by="_source.state" />
            <el-table-column key="Inspection Type" min-width="175" label="Inspection Type" :sortable="true" prop="_source.inspectionType" sort-by="_source.inspectionType" />
            <el-table-column key="Date Scheduled" min-width="190" label="Date Scheduled" :sortable="true" sort-by="_source.dateScheduled">
              <template v-slot="props">
                {{ props.row._source.dateScheduled | formatDate }}
              </template>
            </el-table-column>
            <el-table-column key="Requested Inspection Date" min-width="190" label="Req Insp Date" :sortable="true" sort-by="_source.dateScheduled">
              <template v-slot="props">
                {{ props.row._source.requestedInspectionDate | formatDate }}
              </template>
            </el-table-column>
            <el-table-column key="Actual Inspection Date" min-width="190" label="Actual Insp Date" :sortable="true" sort-by="_source.actualInspectionDate">
              <template v-slot="props">
                {{ props.row._source.actualInspectionDate | formatDate }}
              </template>
            </el-table-column>
            <el-table-column key="Scheduled By" min-width="160" label="Scheduled By" :sortable="true" prop="_source.scheduledBy" sort-by="_source.scheduledBy" />
            <el-table-column key="Inspected By" min-width="160" label="Inspected By" :sortable="true" prop="_source.inspectedBy" sort-by="_source.inspectedBy" />
            <el-table-column key="Inspection Result" min-width="195" label="Inspection Result" :sortable="true" prop="_source.inspectionResult" sort-by="_source.inspectionResult" />
            <el-table-column key="Invoice Number" min-width="175" label="Invoice Number" :sortable="true" prop="_source.invoiceNumber" sort-by="_source.invoiceNumber" />
            <el-table-column key="Hours" min-width="100" label="Hours" :sortable="true" prop="_source.hours" sort-by="_source.hours" />
            <el-table-column key="Price" min-width="100" label="Price" :sortable="true" prop="_source.price" sort-by="_source.price" />
            <el-table-column key="Trip Charge" min-width="150" label="Trip Charge" :sortable="true" prop="_source.tripCharge" sort-by="_source.inspectitripChargeonResult" />
            <el-table-column key="Special Notes" min-width="225" label="Special Notes" :sortable="true" prop="_source.specialNotes" sort-by="_source.specialNotes" />
            <el-table-column
              width="80"
              class="el-table__fixed"
              fixed="right"
              label="Files">
              <template v-slot="props">
                <div v-if="idaInspectionHasForms(props.row)" type="success">
                  <span class="invisible">Forms Path: {{ "props.row._source.dataDirectory" }}</span>
                  <img src="static/img/icon-folder.png" @click.stop="viewFiles(props.row._source.dataDirectory)" title="View Forms" class="files-icon" />
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="col-sm-6 pagination-info">
          <p class="category">Showing {{total}} entries</p>
        </div>
        <div class="col-sm-6">
          <p-button class="pull-right transparent" size="sm" icon @click="resultsNoValidate"></p-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import {
  assembleTableColumnSortProps,
  autocompleteReady,
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  getTableHeight,
  maxResultsWarning,
  sortNested,
  tableHeaderSortCssClass,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import {minFields} from 'src/utilities/validation'
import {sync} from 'vuex-pathify'
import Constants from 'src/utilities/constants'
import PButton from 'src/components/UIComponents/Button.vue'
import Permissions from 'src/utilities/permissions'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import tableColumns from '@/utilities/tableColumns'

export default {
  components: {
    PButton,
    vSelect
  },
  computed: {
    query: sync('idaArchive@query'),
    user: sync('security/user'),
    ...mapGetters({
      autocompleteIdaClients: 'idaArchive/autocompleteResultsClient',
      autocompleteIdaBuilders: 'idaArchive/autocompleteResultsBuilder',
      autocompleteIdaSubdivisions: 'idaArchive/autocompleteResultsCommunity',
      autocompleteIdaStreets: 'idaArchive/autocompleteResultsStreet',
      tableData: 'idaArchive/results'
    }),
    sortedTableData() {
      return this.tableData.sort(sortNested(this.sortedProps))
    },
    filteredTableColumns() {
      return tableColumns.IDA_ARCHIVE
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    },
    hasResults() {
      return this.tableData && this.tableData.length > 0
    },
    total() {
      return this.tableData.length
    },
    canRead() {
      return this.$can('read', Permissions.IDA)
    },
  },
  data() {
    return {
      constants: Constants,
      selectedIdaSubdivision: null,
      selectedIdaClient: null,
      selectedIdaBuilder: null,
      selectedIdaStreet: null,
      selectedInspectionResult: null,
      tableKey: 0,
      mainSearchFieldErrorText: Constants.MESSAGES.AT_LEAST_ONE_FIELD_REQUIRED,
      loading: false,
      tableColumns: tableColumns.IDA_ARCHIVE,
      datePickerConfig: {
        defaultValue: new Date(2004, 5, 1),
        disabledDate(date) {
          const time = date.getTime()
          const start = new Date(2004, 5, 1).getTime()  // June 1, 2004
          const end = new Date(2024, 4, 31, 23, 59, 59).getTime() // May 31, 2024

          // Disable everything outside the range
          return time < start || time > end
        }
      }
    }
  },
  methods: {
    headerSortClass({columnIndex}) {
      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 2)
    },
    showInvalidAutocompleteWarning(refAndPathName) {
      // $ref has to match prop in store
      return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`idaArchive/query@${refAndPathName}`))
    },
    fieldNotValidError(field) {
      return autocompleteTextButNoValueMessage(field)
    },
    downloadExcel() {
      this.$store.dispatch('idaArchive/resultsToExcel')
    },
    getUserDisplayName(userId) {
      return this.$store.getters['users/displayName'](userId)
    },
    clearSearchAndResults() {
      this.$store.commit('idaArchive/resetQuery')
      this.$store.commit('idaArchive/resetResults')
      this.selectedIdaClient = null
      this.selectedIdaBuilder = null
      this.selectedIdaSubdivision = null
      this.selectedIdaStreet = null
      this.selectedInspectionResult = null
      this.$validator.reset()
    },
    async fetchSearchResults() {
      if (await this.$validator.validateAll()) {
        this.loading = true
        const response = await this.$store.dispatch('idaArchive/fetchSearchResults')
        this.loading = false
        if (response.hits.total.value === 0) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
        if (response.hits.total.value > Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS) {
          maxResultsWarning(this, Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS)
        }
      }
    },
    resultsNoValidate() {
      this.$store.dispatch('idaArchive/fetchSearchResults')
    },
    async findIdaClients(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('idaArchive/query@idaClientName', null)
        await this.findClient(text)
        cb(this.autocompleteIdaClients)
      }
    },
    async findIdaBuilders(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('idaArchive/query@idaBuilderName', null)
        await this.findBuilder(text)
        cb(this.autocompleteIdaBuilders)
      }
    },
    async findIdaSubdivisions(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('idaArchive/query@idaSubdivisionName', null)
        await this.findSubdivision(text)
        cb(this.autocompleteIdaSubdivisions)
      }
    },
    async findIdaStreets(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('idaArchive/query@idaStreetName', null)
        await this.findStreet(text)
        cb(this.autocompleteIdaStreets)
      }
    },
    setIdaClient(client) {
      if (client) {
        this.$store.set('idaArchive/query@idaClientName', client.name)
        this.$store.set('idaArchive/autocompleteResultsClient', [])
        this.$validator.reset()
      }
    },
    setIdaBuilder(builder) {
      if (builder) {
        this.$store.set('idaArchive/query@idaBuilderName', builder.name)
        this.$store.set('idaArchive/autocompleteResultsBuilder', [])
        this.$validator.reset()
      }
    },
    setIdaSubdivision(subdivision) {
      if (subdivision) {
        this.$store.set('idaArchive/query@idaSubdivisionName', subdivision.name)
        this.$store.set('idaArchive/autocompleteResultsCommunity', [])
        this.$validator.reset()
      }
    },
    setIdaStreet(street) {
      if (street) {
        this.$store.set('idaArchive/query@idaStreetName', street.name)
        this.$store.set('idaArchive/autocompleteResultsStreet', [])
        this.$validator.reset()
      }
    },
    verifyIdaClientAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'idaArchive/query@idaClientName', 'idaClient', this.selectedIdaClient)
    },
    verifyIdaBuilderAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'idaArchive/query@idaBuilderName', 'idaBuilder', this.selectedIdaBuilder)
    },
    verifyIdaSubdivisionAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'idaArchive/query@idaSubdivisionName', 'idaSubdivision', this.selectedIdaSubdivision)
    },
    verifyIdaStreetAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'idaArchive/query@idaStreetName', 'idaStreet', this.selectedIdaStreet)
    },
    async findClient(text) {
      if (text) {
        await this.$store.dispatch('idaArchive/fetchClientByNameStartsWith', {
          name: text
        })
      }
    },
    async findBuilder(text) {
      if (text) {
        await this.$store.dispatch('idaArchive/fetchBuilderByNameStartsWith', {
          name: text
        })
      }
    },
    async findSubdivision(text) {
      if (text) {
        await this.$store.dispatch('idaArchive/fetchSubdivisionByNameStartsWith', {
          name: text
        })
      }
    },
    async findStreet(text) {
      if (text) {
        await this.$store.dispatch('idaArchive/fetchStreetByNameStartsWith', {
          name: text
        })
      }
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    idaInspectionHasForms(row) {
      return (row._source.dataDirectory && row._source.dataDirectory !== '')
    },
    async viewFiles(idaFilePath) {
      const path = "ida/" + idaFilePath
        .replace(/\./g, '')      // remove all periods
        .replace(/\/{2,}/g, '/') // replace double (or more) slashes with a single slash
      this.$eventHub.$emit('show-modal-1', 'Archive File Browser', path, '600px')
    },
  },
  mounted() {
    this.$store.commit('idaArchive/resetQuery')
    minFields('idaArchive')
    this.$validator.reset()
  },
  watch: {
    filteredTableColumns: function() {
      this.tableKey++
    },
    selectedIdaClient: function(selectedIdaClient) {
      if (!selectedIdaClient) {
        this.$store.set('idaArchive/query@idaClientName', null)
      }
    },
    selectedIdaBuilder: function(selectedIdaBuilder) {
      if (!selectedIdaBuilder) {
        this.$store.set('idaArchive/query@idaBuilderName', null)
      }
    },
    selectedIdaStreet: function(selectedIdaStreet) {
      if (!selectedIdaStreet) {
        this.$store.set('idaArchive/query@idaStreetName', null)
      }
    },
    selectedIdaSubdivision: function(selectedIdaSubdivision) {
      if (!selectedIdaSubdivision) {
        this.$store.set('idaArchive/query@idaSubdivisionName', null)
      }
    }
  }
}
</script>
<style scoped>
  #btn-clear {
    margin: 0 12px;
  }
</style>
